<template>
  <div class="wrong-question-book">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>
        <i class="el-icon-warning-outline"></i>
        课程错题集
      </h2>
      <p class="page-description">显示本门课程所有学生的错题，按错误率排序</p>
    </div>

    <!-- 筛选条件 -->
    <div class="filter-section">
      <el-card class="filter-card">
        <div slot="header" class="filter-header">
          <span><i class="el-icon-filter"></i> 筛选条件</span>
          <el-button type="text" @click="resetFilters" size="small">重置</el-button>
        </div>
        <el-form :model="filters" :inline="true" class="filter-form">
          <el-form-item label="作业">
            <el-select v-model="filters.homeworkId" placeholder="选择作业" clearable @change="applyFilters">
              <el-option
                v-for="homework in filterOptions.homeworks"
                :key="homework.id"
                :label="homework.name"
                :value="homework.id">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="知识点">
            <el-select v-model="filters.knowledge" placeholder="选择知识点" clearable @change="applyFilters">
              <el-option
                v-for="knowledge in filterOptions.knowledges"
                :key="knowledge"
                :label="knowledge"
                :value="knowledge">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="班级">
            <el-select v-model="filters.gradeId" placeholder="选择班级" clearable @change="applyFilters">
              <el-option
                v-for="grade in filterOptions.grades"
                :key="grade.id"
                :label="grade.name"
                :value="grade.id">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="学生">
            <el-select v-model="filters.studentId" placeholder="选择学生" clearable @change="applyFilters">
              <el-option
                v-for="student in filterOptions.students"
                :key="student.id"
                :label="`${student.name} (${student.studentNum})`"
                :value="student.id">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="applyFilters" :loading="loading">
              <i class="el-icon-search"></i> 筛选
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading" element-loading-text="正在加载错题数据..." element-loading-spinner="el-icon-loading" element-loading-background="rgba(255, 255, 255, 0.8)" style="height: 200px;">
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <el-alert
        :title="error"
        type="error"
        show-icon
        :closable="false">
      </el-alert>
      <div class="error-actions">
        <el-button type="primary" @click="loadWrongQuestions">重新加载</el-button>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="!wrongQuestions || wrongQuestions.length === 0" class="empty-container">
      <div class="empty-content">
        <i class="el-icon-document"></i>
        <h3>暂无错题数据</h3>
        <p>本课程还没有学生提交错题记录</p>
      </div>
    </div>

    <!-- 错题列表 -->
    <div v-else class="wrong-questions-content">
      <!-- 统计信息 -->
      <div class="statistics-card">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-item">
              <div class="stat-value">{{ statistics.totalQuestions }}</div>
              <div class="stat-label">错题总数</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-item">
              <div class="stat-value">{{ statistics.totalStudents }}</div>
              <div class="stat-label">学生总数</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-item">
              <div class="stat-value">{{ statistics.totalWrongRecords }}</div>
              <div class="stat-label">错题记录</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-item">
              <div class="stat-value">{{ averageWrongRate }}%</div>
              <div class="stat-label">平均错误率</div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 错题列表 -->
      <div class="wrong-questions-list">
        <div v-for="(question, index) in wrongQuestions" :key="question.questionId" class="question-card">
          <div class="question-header">
            <div class="question-info">
              <span class="question-number">第{{ index + 1 }}题</span>
              <span class="question-chapter" v-if="question.chapter">{{ question.chapter }}</span>
              <span class="question-type">{{ getQuestionTypeText(question.questionType) }}</span>
              <span class="question-difficulty">{{ getDifficultyText(question.questionDifficulty) }}</span>
            </div>
            <div class="question-stats">
              <el-tag :type="getWrongRateType(question.wrongRate)" size="medium">
                {{ question.wrongStudentCount }}/{{ question.totalStudents }} ({{ question.wrongRate }})
              </el-tag>
            </div>
          </div>

          <div class="question-content">
            <div class="question-text" v-html="formatQuestionContent(question.questionContent)"></div>
            <div v-if="question.questionImageUrl" class="question-image">
              <img :src="question.questionImageUrl" alt="题目图片" @error="handleImageError">
            </div>
          </div>

          <!-- 题目选项 -->
          <div v-if="question.questionOptions" class="question-options">
            <div class="options-title">选项：</div>
            <div class="options-content" v-html="formatOptions(question.questionOptions)"></div>
          </div>

          <!-- 答案和解析 -->
          <div class="question-answer">
            <el-collapse>
              <el-collapse-item title="查看标准答案和解析" name="answer">
                <div class="answer-content">
                  <div class="standard-answer">
                    <strong>标准答案：</strong>
                    <span v-html="formatAnswer(question.questionStandardAnswer)"></span>
                  </div>
                  <div v-if="question.questionAnalysis" class="question-analysis">
                    <strong>题目解析：</strong>
                    <div v-html="formatAnalysis(question.questionAnalysis)"></div>
                  </div>
                  <div v-if="question.relatedKnowledge" class="related-knowledge">
                    <strong>相关知识点：</strong>
                    <span>{{ question.relatedKnowledge }}</span>
                  </div>
                </div>
              </el-collapse-item>
            </el-collapse>
          </div>

          <!-- 错题详情 -->
          <div class="wrong-details">
            <el-collapse>
              <el-collapse-item :title="`查看错题详情 (${question.wrongRecords.length}条记录)`" name="details">
                <div class="wrong-records">
                  <div v-for="record in question.wrongRecords" :key="record.wrongRecordId" class="wrong-record">
                    <div class="record-header">
                      <span class="student-name">{{ record.studentName }}</span>
                      <span class="student-num">{{ record.studentNum }}</span>
                      <span class="wrong-time">{{ formatTime(record.wrongTime) }}</span>
                    </div>
                    <div class="record-content">
                      <div v-if="record.studentAnswerText" class="student-answer">
                        <strong>学生答案：</strong>
                        <span v-html="formatAnswer(record.studentAnswerText)"></span>
                      </div>
                      <div v-if="record.studentAnswerImageUrl" class="student-answer-image">
                        <strong>学生答案图片：</strong>
                        <img :src="record.studentAnswerImageUrl" alt="学生答案" @error="handleImageError">
                      </div>
                      <div v-if="record.teacherComment" class="teacher-comment">
                        <strong>教师评语：</strong>
                        <span>{{ record.teacherComment }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </el-collapse-item>
            </el-collapse>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCourseWrongQuestions, getCourseWrongQuestionsFilterOptions } from '@/api/homeworkSubmit'

export default {
  name: 'WrongQuestionBook',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      wrongQuestions: [],
      statistics: {
        totalQuestions: 0,
        totalStudents: 0,
        totalWrongRecords: 0
      },
      filters: {
        homeworkId: null,
        knowledge: null,
        examId: null,
        gradeId: null,
        studentId: null
      },
      filterOptions: {
        homeworks: [],
        knowledges: [],
        grades: [],
        students: []
      }
    }
  },
  computed: {
    averageWrongRate() {
      if (!this.wrongQuestions || this.wrongQuestions.length === 0) {
        return 0
      }
      const totalRate = this.wrongQuestions.reduce((sum, question) => {
        const rate = parseFloat(question.wrongRate.replace('%', ''))
        return sum + rate
      }, 0)
      return (totalRate / this.wrongQuestions.length).toFixed(1)
    }
  },
  mounted() {
    this.loadFilterOptions()
    this.loadWrongQuestions()
  },
  methods: {
    async loadWrongQuestions() {
      if (!this.courseId) {
        this.error = '课程ID不能为空'
        return
      }

      this.loading = true
      this.error = null

      try {
        console.log('开始加载课程错题集，课程ID:', this.courseId, '筛选条件:', this.filters)
        const response = await getCourseWrongQuestions(this.courseId, this.filters)
        console.log('错题集API响应:', response)

        if (response.code === 1) {
          this.wrongQuestions = response.data.uniqueQuestions || []
          this.statistics = response.data.statistics || {}
          console.log('错题集加载成功:', {
            questions: this.wrongQuestions.length,
            statistics: this.statistics
          })
        } else {
          this.error = response.msg || '获取错题集失败'
        }
      } catch (error) {
        console.error('加载错题集失败:', error)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },

    // 加载筛选选项
    async loadFilterOptions() {
      if (!this.courseId) {
        return
      }

      try {
        console.log('开始加载筛选选项，课程ID:', this.courseId)
        const response = await getCourseWrongQuestionsFilterOptions(this.courseId)
        console.log('筛选选项API响应:', response)

        if (response.code === 1) {
          this.filterOptions = response.data || {
            homeworks: [],
            knowledges: [],
            grades: [],
            students: []
          }
          console.log('筛选选项加载成功:', this.filterOptions)
        } else {
          console.error('筛选选项加载失败:', response.msg)
        }
      } catch (error) {
        console.error('加载筛选选项失败:', error)
      }
    },

    // 应用筛选
    applyFilters() {
      console.log('应用筛选条件:', this.filters)
      this.loadWrongQuestions()
    },

    // 重置筛选条件
    resetFilters() {
      this.filters = {
        homeworkId: null,
        knowledge: null,
        examId: null,
        gradeId: null,
        studentId: null
      }
      console.log('重置筛选条件')
      this.loadWrongQuestions()
    },

    getQuestionTypeText(type) {
      const typeMap = {
        1: '单选题',
        2: '多选题',
        3: '判断题',
        4: '填空题',
        5: '简答题',
        6: '计算题'
      }
      return typeMap[type] || '未知类型'
    },

    getDifficultyText(difficulty) {
      const difficultyMap = {
        1: '简单',
        2: '中等',
        3: '困难'
      }
      return difficultyMap[difficulty] || '未知难度'
    },

    getWrongRateType(rate) {
      const rateNum = parseFloat(rate.replace('%', ''))
      if (rateNum >= 70) return 'danger'
      if (rateNum >= 40) return 'warning'
      return 'success'
    },

    formatQuestionContent(content) {
      if (!content) return ''
      return content.replace(/\n/g, '<br>')
    },

    formatOptions(options) {
      if (!options) return ''
      try {
        const optionsObj = JSON.parse(options)
        let html = ''
        for (const [key, value] of Object.entries(optionsObj)) {
          html += `<div class="option-item"><strong>${key}.</strong> ${value}</div>`
        }
        return html
      } catch (e) {
        return options
      }
    },

    formatAnswer(answer) {
      if (!answer) return ''
      return answer.replace(/\n/g, '<br>')
    },

    formatAnalysis(analysis) {
      if (!analysis) return ''
      return analysis.replace(/\n/g, '<br>')
    },

    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString('zh-CN')
    },

    handleImageError(event) {
      event.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.wrong-question-book {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.page-header h2 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 24px;
}

.page-header h2 i {
  margin-right: 8px;
  color: #E6A23C;
}

.page-description {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  color: #303133;
}

.filter-header i {
  margin-right: 5px;
  color: #409EFF;
}

.filter-form {
  margin-top: 10px;
}

.filter-form .el-form-item {
  margin-bottom: 10px;
}

.filter-form .el-select {
  width: 200px;
}

.loading-container,
.error-container,
.empty-container {
  background: white;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-actions {
  margin-top: 20px;
}

.empty-content i {
  font-size: 64px;
  color: #C0C4CC;
  margin-bottom: 16px;
}

.empty-content h3 {
  margin: 0 0 8px 0;
  color: #606266;
}

.empty-content p {
  margin: 0;
  color: #909399;
}

.statistics-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-item {
  text-align: center;
  padding: 10px;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #606266;
}

.wrong-questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s;
}

.question-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #EBEEF5;
}

.question-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.question-number {
  font-weight: bold;
  color: #409EFF;
  font-size: 16px;
}

.question-chapter,
.question-type,
.question-difficulty {
  padding: 2px 8px;
  background: #F0F9FF;
  color: #409EFF;
  border-radius: 4px;
  font-size: 12px;
}

.question-content {
  margin-bottom: 15px;
}

.question-text {
  font-size: 16px;
  line-height: 1.6;
  color: #303133;
  margin-bottom: 10px;
}

.question-image img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.question-options {
  margin-bottom: 15px;
  padding: 15px;
  background: #F8F9FA;
  border-radius: 6px;
}

.options-title {
  font-weight: bold;
  margin-bottom: 10px;
  color: #606266;
}

.option-item {
  margin-bottom: 5px;
  line-height: 1.5;
}

.question-answer,
.wrong-details {
  margin-top: 15px;
}

.answer-content {
  padding: 15px;
  background: #F0F9FF;
  border-radius: 6px;
}

.standard-answer,
.question-analysis,
.related-knowledge {
  margin-bottom: 10px;
}

.standard-answer:last-child,
.question-analysis:last-child,
.related-knowledge:last-child {
  margin-bottom: 0;
}

.wrong-records {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.wrong-record {
  padding: 15px;
  background: #FEF0F0;
  border-radius: 6px;
  border-left: 4px solid #F56C6C;
}

.record-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
  font-size: 14px;
}

.student-name {
  font-weight: bold;
  color: #303133;
}

.student-num {
  color: #606266;
}

.wrong-time {
  color: #909399;
  margin-left: auto;
}

.record-content {
  font-size: 14px;
}

.student-answer,
.student-answer-image,
.teacher-comment {
  margin-bottom: 8px;
}

.student-answer:last-child,
.student-answer-image:last-child,
.teacher-comment:last-child {
  margin-bottom: 0;
}

.student-answer-image img {
  max-width: 200px;
  height: auto;
  border-radius: 4px;
  margin-top: 5px;
}

.teacher-comment {
  color: #E6A23C;
}
</style>
